import { boundsOfPoints } from './bounds';
import { projectParameter } from './projection';
import type { ShapeOps } from './shape-ops';
import {
  add,
  angleOf,
  dist,
  dot,
  lerp,
  normalize,
  perp,
  reflectAcross,
  rotateAbout,
  scale,
  sub,
  type Vec2,
} from './vec2';

export interface Segment {
  readonly type: 'line';
  readonly a: Vec2;
  readonly b: Vec2;
}

export const segment = (a: Vec2, b: Vec2): Segment => ({ type: 'line', a, b });

export function closestPointOnSegment(s: Segment, p: Vec2): Vec2 {
  const t = projectParameter(s.a, sub(s.b, s.a), p);
  if (t === null) return s.a;
  return lerp(s.a, s.b, Math.min(1, Math.max(0, t)));
}

export const SEGMENT_OPS: ShapeOps<Segment> = {
  measurements: (s) => [
    { label: 'Length', kind: 'length', value: dist(s.a, s.b) },
    { label: 'Angle', kind: 'angle', value: angleOf(sub(s.b, s.a)) },
    { label: 'Δx', kind: 'length', value: s.b.x - s.a.x },
    { label: 'Δy', kind: 'length', value: s.b.y - s.a.y },
    { label: 'Start', kind: 'point', value: s.a },
    { label: 'End', kind: 'point', value: s.b },
    { label: 'Midpoint', kind: 'point', value: lerp(s.a, s.b, 0.5) },
  ],
  endpoints: (s) => [s.a, s.b],
  midpoint: (s) => lerp(s.a, s.b, 0.5),
  center: () => null,
  quadrants: () => [],
  closestPoint: closestPointOnSegment,
  bounds: (s) => boundsOfPoints([s.a, s.b]),
  carrier: (s) => ({ kind: 'line', a: s.a, b: s.b }),
  liesOn: (s, p, eps) => dist(closestPointOnSegment(s, p), p) <= eps,
  perpendicularFrom: (s, from) => {
    const t = projectParameter(s.a, sub(s.b, s.a), from);
    if (t === null || t < 0 || t > 1) return [];
    return [lerp(s.a, s.b, t)];
  },
  tangentFrom: () => [],
  offset: (s, distance) => {
    const normal = leftNormal(s);
    if (normal.x === 0 && normal.y === 0) return null;
    const shift = scale(normal, distance);
    return segment(add(s.a, shift), add(s.b, shift));
  },
  offsetDistance: (s, p) => dot(leftNormal(s), sub(p, s.a)),
  translate: (s, delta) => segment(add(s.a, delta), add(s.b, delta)),
  rotate: (s, center, degrees) =>
    segment(rotateAbout(s.a, center, degrees), rotateAbout(s.b, center, degrees)),
  mirror: (s, a, b) => segment(reflectAcross(s.a, a, b), reflectAcross(s.b, a, b)),
  parameterOf: (s, p) => projectParameter(s.a, sub(s.b, s.a), p) ?? 0,
  period: () => null,
  piece: (s, t0, t1) =>
    segment(t0 === 0 ? s.a : lerp(s.a, s.b, t0), t1 === 1 ? s.b : lerp(s.a, s.b, t1)),
};

function leftNormal(s: Segment): Vec2 {
  return perp(normalize(sub(s.b, s.a)));
}
